import { useEffect } from 'react'

import { createTrackingMessage, sendMessage } from './tracking.ts'

/**
 * Custom hook to track Trustpilot widget clicks
 *
 * This hook handles the complex logic required to track clicks on the Trustpilot widget
 * which is loaded in an iframe from a different origin.
 *
 * Why this complex approach is necessary:
 * 1. The Trustpilot widget is loaded in an iframe, making it impossible to attach event listeners directly
 * 2. The iframe content is from a different origin (trustpilot.com), so we can't access its DOM
 * 3. The widget loads asynchronously, so we need to wait for it to be ready
 * 4. We need to capture clicks before they reach the iframe to emit our tracking event
 * 5. The overlay must be positioned exactly over the widget (148px x 86px) to avoid interfering with other elements
 */
export function useStartTrackingClicksOnTrustpilotWidget(): void {
  useEffect(() => {
    const setupTrustpilotOverlay = () => {
      const trustpilotContainer = document.querySelector('.trustpilot-widget')
      if (!trustpilotContainer) {
        setTimeout(setupTrustpilotOverlay, 1000)
        return
      }

      // Clean up any existing overlays first
      // This is necessary because the useEffect can run multiple times, creating duplicate overlays
      const existingOverlays = trustpilotContainer.querySelectorAll('[style*="z-index: 1000"]')
      existingOverlays.forEach((overlay) => overlay.remove())

      // Create invisible overlay that captures clicks
      const overlay = document.createElement('div')
      overlay.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        width: 148px;
        height: 86px;
        z-index: 1000;
        background: transparent;
        cursor: pointer;
        pointer-events: auto;
      `

      overlay.addEventListener('click', (_event) => {
        // Send tracking event
        sendMessage(
          createTrackingMessage({
            event: 'trustpilot_widget_clicked',
          }),
        )

        // Find the Trustpilot iframe and construct the profile URL
        // We can't access the iframe content directly, but we can extract the business unit ID
        // from the iframe src attribute to construct the correct Trustpilot profile URL
        const trustpilotIframe = trustpilotContainer.querySelector('iframe[src*="trustpilot.com"]') as HTMLIFrameElement

        if (trustpilotIframe) {
          // Extract the business unit ID from the iframe URL to construct the Trustpilot profile URL
          const src = trustpilotIframe.src
          const businessUnitMatch = src.match(/businessunitId=([^&]+)/)
          if (businessUnitMatch) {
            const trustpilotUrl = `https://fr.trustpilot.com/review/orus.eu?utm_medium=trustbox&utm_source=Mini`
            window.open(trustpilotUrl, '_blank', 'noopener,noreferrer')
          }
        }
      })

      // Make sure container has relative positioning for absolute overlay positioning
      const containerStyle = window.getComputedStyle(trustpilotContainer)
      if (containerStyle.position === 'static') {
        ;(trustpilotContainer as HTMLElement).style.position = 'relative'
      }

      trustpilotContainer.appendChild(overlay)

      return () => {
        if (overlay.parentNode) {
          overlay.parentNode.removeChild(overlay)
        }
      }
    }

    // Try to setup overlay immediately and with delays
    // Multiple attempts are necessary because the Trustpilot widget loads asynchronously
    // and we need to wait for it to be fully rendered before we can attach our overlay
    let cleanup = setupTrustpilotOverlay()

    if (!cleanup) {
      const timeout1 = setTimeout(() => {
        cleanup = setupTrustpilotOverlay()
      }, 2000)

      const timeout2 = setTimeout(() => {
        cleanup = setupTrustpilotOverlay()
      }, 5000)

      return () => {
        clearTimeout(timeout1)
        clearTimeout(timeout2)
        if (cleanup) cleanup()
      }
    }

    return cleanup
  }, [])
}
